/**
 * Utilidades para resetear el viewport en iOS Safari sin refrescar la página.
 *
 * ¿Por qué es necesario? En iOS, si un input tiene font-size < 16px y el
 * usuario lo enfoca, el navegador hace zoom automático y queda "atascado"
 * tras el blur. También a veces el usuario hace pinch-zoom y quiere volver.
 *
 * ¿Cómo funciona? iOS Safari respeta cambios DINÁMICOS al meta viewport
 * (aunque ignore los estáticos como user-scalable=no). Al forzar
 * maximum-scale=1 por un frame y devolverlo, se fuerza un re-layout que
 * resetea la escala actual a 1.
 *
 * Uso:
 *   - Automático: montar <ViewportResetOnNavigation /> en el layout.
 *   - Manual: llamar resetViewport() en cualquier botón/acción.
 */

const BASE_VIEWPORT = "width=device-width, initial-scale=1, viewport-fit=cover";
const LOCK_VIEWPORT = "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover";

let lastResetAt = 0;
const MIN_INTERVAL_MS = 250; // Evita spam de resets en bursts de eventos.

/**
 * Fuerza al navegador a volver a escala 1. Idempotente y seguro en SSR.
 * Debounced a 250ms para evitar flickers al disparar muchas veces seguidas.
 */
export function resetViewport(): void {
  if (typeof document === "undefined") return;

  const now = Date.now();
  if (now - lastResetAt < MIN_INTERVAL_MS) return;
  lastResetAt = now;

  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta) return;

  const original = meta.getAttribute("content") || BASE_VIEWPORT;

  // Paso 1: lock al 100% (fuerza re-layout instantáneo en iOS)
  meta.setAttribute("content", LOCK_VIEWPORT);

  // Paso 2: restaurar para permitir pinch-zoom manual después
  // (dos frames para asegurar que iOS procesó el cambio)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      meta.setAttribute("content", original);
    });
  });
}

/**
 * ¿Está el usuario actualmente con zoom distinto de 1? Heurística:
 * compara window.visualViewport.scale vs 1.
 */
export function isZoomed(): boolean {
  if (typeof window === "undefined") return false;
  const vv = window.visualViewport;
  if (!vv) return false;
  return Math.abs(vv.scale - 1) > 0.02;
}
